/**
 * Billing Analytics Service
 * Collection reconciliation reports proxied via RIS backend → registration service.
 */

import { API_CONFIG, API_ENDPOINTS } from '../config/api.config';
import authService from '../auth/authService';
import { apiGet } from '../utils/apiClient';

export interface BillingAnalyticsBreakdownItem {
  label: string;
  amount: number;
  count?: number;
}

export interface BillingAnalyticsSummary {
  totalCollection: number;
  refunds: number;
  discounts: number;
  netCollection: number;
  transactionCount: number;
}

export interface BillingAnalyticsReconciliation {
  summary: BillingAnalyticsSummary;
  byUser: BillingAnalyticsBreakdownItem[];
  byPaymentMode: BillingAnalyticsBreakdownItem[];
  byScheme: BillingAnalyticsBreakdownItem[];
  byCounter: BillingAnalyticsBreakdownItem[];
  filters?: {
    startDate: string;
    endDate: string;
  };
  generatedAt?: string;
}

export interface BillingAnalyticsQuery {
  startDate: string;
  endDate: string;
  counterId?: string;
  userId?: string;
  paymentMode?: string;
  schemeId?: string;
}

function buildQueryString(params: BillingAnalyticsQuery & { format?: string }): string {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value != null && String(value).trim() !== '') {
      search.append(key, String(value));
    }
  });
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

export async function getBillingReconciliation(
  query: BillingAnalyticsQuery
): Promise<{ success: boolean; data?: BillingAnalyticsReconciliation; error?: string }> {
  try {
    const url = `${API_ENDPOINTS.BILLING_ANALYTICS.RECONCILIATION}${buildQueryString(query)}`;
    const response = await apiGet<{
      success?: boolean;
      data?: BillingAnalyticsReconciliation;
      error?: string;
    }>(url, { requireAuth: true });
    const payload = response.data;
    if (payload?.success === false) {
      return { success: false, error: payload.error || 'Failed to load reconciliation data' };
    }
    const data = payload?.data ?? (payload as unknown as BillingAnalyticsReconciliation);
    if (data?.summary) {
      return { success: true, data };
    }
    return { success: false, error: 'Invalid reconciliation response' };
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Failed to load reconciliation data',
    };
  }
}

export async function exportBillingReconciliationCsv(query: BillingAnalyticsQuery): Promise<void> {
  const url = `${API_ENDPOINTS.BILLING_ANALYTICS.RECONCILIATION_EXPORT}${buildQueryString({
    ...query,
    format: 'csv',
  })}`;
  const authHeader = authService.getAuthHeader();
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      accept: 'text/csv, application/json',
      'tenant-id': API_CONFIG.TENANT_ID,
      ...(authHeader.Authorization ? { Authorization: authHeader.Authorization } : {}),
    },
    mode: 'cors',
  });

  if (!response.ok) {
    let message = `Export failed (${response.status})`;
    try {
      const json = await response.json();
      message = json?.error || json?.message || message;
    } catch {
      // ignore
    }
    throw new Error(message);
  }

  const blob = await response.blob();
  const disposition = response.headers.get('content-disposition') || '';
  const match = disposition.match(/filename="?([^"]+)"?/i);
  const filename =
    match?.[1] || `collection-reconciliation-${query.startDate}-${query.endDate}.csv`;
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
}
